import SVGIcon from 'components/SVGIcon/SVGIcon';
import QuantityInput from 'containers/QuantityInput';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Language, Product, TrackingAddToCartAddFromType } from 'types/schema';
import { allowDecimalQuantity } from 'utils/allowDecimalQuantity';
import generateProductSlug from 'utils/generateProductSlug';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { parse } from 'utils/queryString';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { DetailsContainer } from '../Details/DetailsContainer';
import KamereoLogo from './kamereoLogo.svg';
import { Price } from './Price';
import translations from 'translations';

const Title = styled.span`
  overflow: hidden;
  font-size: ${({ titleSize }) => (titleSize ? titleSize : '14')}px;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 22px;
  max-height: 42px;
  color: #111827;
  font-weight: 500;
  margin: 4px 0;
  @media (max-width: 767px) {
    font-size: 11px;
    line-height: 16px;
  }
`;

const SubTitle = styled.p`
  overflow: hidden;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: #6b7280;
  width: fit-content;
  border-radius: 4px;
  height: 20px;
  @media (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
    height: 16px;
  }
`;
const Header = styled.h3`
  margin-right: ${({ marginRight }) => marginRight};
  margin-bottom: 6px;
  height: 70px;
  ${media.md`
    padding-right: 0;
  `};
  @media (max-width: 767px) {
    height: 56px;
  }
`;

const FooterContainer = styled.div`
  flex: 1;
  display: flex;
  width: 100%;
  padding: ${({ hasHorizontalPadding }) => (hasHorizontalPadding ? '0' : '0 8px')};
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  ${({ isHomePage }) =>
    isHomePage &&
    `
    column-gap: 10px;
  `}
`;

const TagExclusiveWrapper = styled.div`
  color: #424242;
  font-size: 12px;
  font-weight: 400;
`;
const TagExclusive = styled.div`
  padding: 4px 8px;
  background: #e6d8bb;
  color: #806e44;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  width: fit-content;
`;

const SaleContact = styled.div`
  border: 1px solid #aaacb2;
  border-radius: 8px;
  padding: 6px 8px;
  cursor: pointer;
  a {
    color: #595959;
    text-decoration: none;
  }
`;

const addToCartPermission = 'ORDER_PLACE';

const ProductTitleTag = styled.span`
  display: inline;
  font-size: 12px;
  font-weight: 400;
  color: #ffffff;
  background: #4a8cf8;
  padding: 0.8px 4px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
  @media (max-width: 991px) {
    font-size: 8px;
    line-height: 10px;
    padding: 1px 3px;
  }
`;

const KamereoLabel = styled.span`
  display: inline-flex;
  background: #efffd6;
  padding: 0px 4px;
  border-radius: 2px;
  height: 16px;
  line-height: 16px;
  position: relative;
  top: 1.7px;
  margin-right: 4px;
  @media (max-width: 991px) {
    height: 12px;
    top: 2.5px;
    & svg {
      width: 10px !important;
    }
  }
`;

const TotalQuantity = styled.div`
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 991px) {
    height: 18px;
  }
`;

const EmptyQuantity = styled.div`
  height: 54px;
`;

interface IProps {
  product: Product;
  loggedIn: boolean;
  noImage?: boolean;
  permissions: string[];
  onOpenNote: () => void;
  onDeleteNote: () => void;
  addFrom: TrackingAddToCartAddFromType;
  tag?: string | null;
  isHideNote?: boolean;
  isProductDetail?: boolean;
  position?: number;
  listName?: string;
  itemDiscountExists?: boolean;
  added?: number;
  titleSize?: number;
  isHideQuantity?: boolean;
  hasHorizontalPadding?: boolean;
}

const Details: FC<IProps> = (props) => {
  const {
    loggedIn,
    noImage,
    product,
    permissions,
    addFrom,
    tag,
    isProductDetail,
    position,
    listName,
    titleSize,
    isHideQuantity,
    hasHorizontalPadding,
  } = props;
  const { name, inStock, uom, private: isPrivate, allowPurchase, baseUomPrice, baseUom, priceV2, tags } = product;
  const lang = localStorage.getItem('lang') || Language.Vi;
  const [openUomKg, setOpenUomKg] = useState(false);

  const addToCartGa4Callback = useCallback(
    (value) => {
      if (listName === 'Market Page Product List') {
        sendGA4Event(EventName.ADD_TO_CART_FROM_SEARCH, {
          product_id: product.id,
          quantity: value,
          page_number: parse(location.search).page ? Number(parse(location.search).page) : 1,
          source: 'search_result',
          position: (position || 0) + 1,
          keyword: parse(location.search).q || '',
          price: product.price,
        });
      }
    },
    [product, position, listName],
  );

  const QuantityComponent = useMemo(() => {
    return isPrivate && !allowPurchase ? (
      <SaleContact>
        <a rel="noopener noreferrer" target="_blank" href="https://zalo.me/2357831519751856428">
          <FormattedMessage {...utilsMessages.talkToSale} />
        </a>
      </SaleContact>
    ) : inStock ? (
      <div style={{ width: '100%', maxWidth: '220px' }}>
        <QuantityInput
          product={product}
          size="small"
          willOverflow
          allowDecimal={allowDecimalQuantity(uom)}
          addFrom={addFrom}
          tag={tag}
          ga4Callback={addToCartGa4Callback}
          isHomePage
          isFullWidth
          isShowTotalQuantity={true}
        />
      </div>
    ) : (
      <EmptyQuantity style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '220px' }}>
        <TotalQuantity></TotalQuantity>
      </EmptyQuantity>
    );
  }, [inStock, product, uom, addFrom, tag, allowPurchase, isPrivate, addToCartGa4Callback]);

  const tagsTitle = useMemo(() => {
    const tagsResult: React.ReactNode[] = [];
    const newProductTag = (
      <ProductTitleTag>
        <FormattedMessage {...utilsMessages.newTag} />
        {/* {translations(utilsMessages.newTag)} */}
      </ProductTitleTag>
    );
    const kamereoLabelTag = (
      <KamereoLabel>
        <SVGIcon src={KamereoLogo} renderAsImg={false} />
      </KamereoLabel>
    );
    if (tags?.length && tags.includes('NEW_PRODUCTS')) {
      tagsResult.push(newProductTag);
    }
    if (tags?.length && tags.includes('THUONG_HIEU_KAMEREO')) {
      tagsResult.push(kamereoLabelTag);
    }
    return tagsResult;
  }, []);

  const { uomInfo, netUom: currNetUom, uom: currUom } = useProductUomInfo(product, '1');

  return (
    <DetailsContainer hasHorizontalPadding={hasHorizontalPadding}>
      {/* {itemDiscountExists ? (
        <DiscountTagWrapper>
          <DiscountTag>{getLimitedTimeOfferPercent(priceV2?.originalPrice || 0, priceV2?.price || 0)}</DiscountTag>
          {priceV2 && priceV2?.originalPrice > priceV2?.price ? (
            <BasePrice>
              {formatMoney(calculateVAT(priceV2?.originalPrice, priceV2?.vat, priceV2?.vatIncluded).beforeVat)}đ
            </BasePrice>
          ) : null}
        </DiscountTagWrapper>
      ) : (
        <DiscountTagWrapper></DiscountTagWrapper>
      )} */}
      {isPrivate && !allowPurchase ? (
        <TagExclusiveWrapper>
          <TagExclusive>
            <FormattedMessage {...utilsMessages.exclusive} />
          </TagExclusive>
          <FormattedMessage {...utilsMessages.onlyForSpecialBusiness} />
        </TagExclusiveWrapper>
      ) : (
        <Price
          product={product}
          uom={currUom}
          baseUom={baseUom || ''}
          baseUomPrice={baseUomPrice || 0}
          netUom={currNetUom}
          hasHorizontalPadding={hasHorizontalPadding}
        />
      )}
      <Header title={name} marginRight={noImage ? '44pt' : ''}>
        <Link to={`/products/${generateProductSlug(name, product.supplierInternalCode, lang)}`}>
          <Title isProductDetail={isProductDetail} titleSize={titleSize}>
            {tagsTitle.map((tag, idx) => (
              <React.Fragment key={idx}>{tag}</React.Fragment>
            ))}
            {name}
          </Title>
        </Link>
        <SubTitle>{uomInfo}</SubTitle>
      </Header>
      {!isHideQuantity && (
        <FooterContainer hasHorizontalPadding={hasHorizontalPadding}>
          {/* {loggedIn && note && !isHideNote && <NoteSm>{note}</NoteSm>} */}
          <FlexContainer isHomePage={window.location.pathname === '/'}>
            {/* {loggedIn && !isHideNote && (
            <ProductNote isShowNote={false} isOnlyShowNoteIcon={true} note={note} onClickOpenNote={onOpenNote} />
          )} */}
            {(!loggedIn || permissions.indexOf(addToCartPermission) > -1) && QuantityComponent}
          </FlexContainer>
        </FooterContainer>
      )}
    </DetailsContainer>
  );
};

export default Details;
